/**
 * At-Risk widget — Figma frame 175:828 (standalone 500×420 card + compact
 * rail on Cohort Dashboard).
 *
 * Design decisions locked by the Figma spec:
 *   • Uniform amber-tint reason chips regardless of severity — the severity
 *     dot on the avatar is the sole cue. Keeps the rail visually calm.
 *   • "Reach out" as the primary row CTA (deep-links into Messages).
 *   • No emoji, no illustrations. Initials-only avatars.
 *   • Footer link "See all N →" navigates to the full at-risk queue view.
 *     (Compact / rail variant only.)
 *
 * Backend contract: `useAtRisk()` + `useDismissStudent()`.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAtRisk, useDismissStudent } from '../hooks/useAtRisk';
import type {
  AtRiskStudent,
  RiskReason,
  RiskReasonType,
  Severity,
} from '../services/atRiskApi';
import styles from './AtRiskWidget.module.css';

type FilterKey = 'all' | 'high' | 'inactive' | 'hand_raised';

interface AtRiskWidgetProps {
  /** Compact rail variant (used on Cohort Dashboard). */
  compact?: boolean;
  /** Fixed-size standalone card variant (500×420). */
  standalone?: boolean;
  cohortId?: string;
}

const REASON_LABEL: Record<RiskReasonType, string> = {
  lab_time: 'Stuck on lab',
  low_pass_rate: 'Low pass rate',
  missed_deadline: 'Missed deadline',
  hand_raised: 'Hand raised',
  inactive: 'Inactive',
  repeated_failures: 'Repeated failures',
};

const REASON_SHORT: Record<RiskReasonType, (r: RiskReason) => string> = {
  missed_deadline: (r) => `Missed ${extractDays(r.detail) ?? 'deadline'}`,
  inactive: (r) => `Missed ${extractDays(r.detail) ?? '3 days'}`,
  lab_time: (r) => `Stuck ${extractHours(r.detail) ?? 'on lab'}`,
  low_pass_rate: (r) => `Low mastery${extractPct(r.detail) ? ` · ${extractPct(r.detail)}` : ''}`,
  hand_raised: () => 'Hand raised',
  repeated_failures: (r) => `Lab failing ${extractFailCount(r.detail) ?? 'multiple times'}`,
};

function extractDays(text: string): string | null {
  const m = text.match(/(\d+)\s*day/);
  return m ? `${m[1]} days` : null;
}
function extractHours(text: string): string | null {
  const m = text.match(/([\d.]+)\s*hour/);
  return m ? `${m[1]}h` : null;
}
function extractPct(text: string): string | null {
  const m = text.match(/(\d+)%/);
  return m ? `${m[1]}%` : null;
}
function extractFailCount(text: string): string | null {
  const m = text.match(/(\d+)\s*times?/);
  return m ? `${m[1]}x` : null;
}

const SEVERITY_WEIGHT: Record<Severity, number> = { low: 1, medium: 2, high: 3 };

const FILTER_CHIPS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'high', label: 'High priority' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'hand_raised', label: 'Hand raised' },
];

const AtRiskWidget: React.FC<AtRiskWidgetProps> = ({ compact = false, standalone = false, cohortId }) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [openDismissFor, setOpenDismissFor] = useState<string | null>(null);
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useAtRisk(cohortId ? { cohortId } : {});
  const dismissMutation = useDismissStudent();

  const visible = useMemo(() => {
    if (!data) return [];
    let list = [...data];
    if (filter === 'high') list = list.filter((s) => s.aggregateSeverity === 'high');
    if (filter === 'inactive') list = list.filter((s) => s.reasons.some((r) => r.type === 'inactive'));
    if (filter === 'hand_raised') list = list.filter((s) => s.reasons.some((r) => r.type === 'hand_raised'));
    list.sort((a, b) => {
      const sevDiff = SEVERITY_WEIGHT[b.aggregateSeverity] - SEVERITY_WEIGHT[a.aggregateSeverity];
      if (sevDiff !== 0) return sevDiff;
      return b.reasons.length - a.reasons.length;
    });
    return list;
  }, [data, filter]);

  const totalCount = data?.length ?? 0;
  const displayList = compact || standalone ? visible.slice(0, 5) : visible;
  const remaining = totalCount - displayList.length;

  const handleDismiss = (studentId: string, cohortId?: string) => {
    setOpenDismissFor(null);
    // Backend suppression is per-student for 24h (keyed by student + cohort).
    dismissMutation.mutate({ studentId, cohortId });
  };

  const cardClass = [
    styles.card,
    compact ? styles.compact : '',
    standalone ? styles.standalone : '',
  ].filter(Boolean).join(' ');

  return (
    <section className={cardClass} aria-label="At-risk students">
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>At-risk students</h3>
          {totalCount > 0 && (
            <span
              className={styles.countBadge}
              aria-label={`${totalCount} students at risk`}
            >
              {totalCount}
            </span>
          )}
        </div>
        <span className={styles.headerMeta}>Sorted by risk</span>
      </header>

      {!compact && !standalone && (
        <div className={styles.filterChips} role="tablist" aria-label="Filter">
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip.key}
              role="tab"
              aria-selected={filter === chip.key}
              className={`${styles.chip} ${filter === chip.key ? styles.chipActive : ''}`}
              onClick={() => setFilter(chip.key)}
              type="button"
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className={styles.list} aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <div className={`${styles.skeletonAvatar} ${styles.shimmer}`} />
              <div className={styles.skeletonContent}>
                <div className={`${styles.skeletonLine} ${styles.shimmer}`} style={{ width: '40%' }} />
                <div className={`${styles.skeletonLine} ${styles.shimmer}`} style={{ width: '70%' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && !isLoading && (
        <div className={styles.errorState} role="alert">
          <p>Couldn&apos;t load risk signals.</p>
          <button type="button" className={styles.retryBtn} onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && displayList.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon} aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className={styles.emptyText}>
            {totalCount === 0
              ? 'All clear — no students need attention right now.'
              : 'No students match this filter.'}
          </p>
        </div>
      )}

      {!isLoading && !isError && displayList.length > 0 && (
        <ul className={styles.list}>
          {displayList.map((student) => (
            <StudentRow
              key={student.studentId}
              student={student}
              dismissOpen={openDismissFor === student.studentId}
              onToggleDismiss={() =>
                setOpenDismissFor((prev) => (prev === student.studentId ? null : student.studentId))
              }
              onDismiss={() => handleDismiss(student.studentId, student.cohortId)}
              onReachOut={() => navigate(`/messages?studentId=${student.studentId}`)}
            />
          ))}
        </ul>
      )}

      {(compact || standalone) && remaining > 0 && (
        <Link to={cohortId ? `/at-risk?cohortId=${encodeURIComponent(cohortId)}` : '/at-risk'} className={styles.footerLink}>
          See all {totalCount} →
        </Link>
      )}
    </section>
  );
};

// ---------- StudentRow ----------

interface StudentRowProps {
  student: AtRiskStudent;
  dismissOpen: boolean;
  onToggleDismiss: () => void;
  onDismiss: () => void;
  onReachOut: () => void;
}

const StudentRow: React.FC<StudentRowProps> = ({
  student,
  dismissOpen,
  onToggleDismiss,
  onDismiss,
  onReachOut,
}) => {
  const initials = student.studentName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const primaryReason = student.reasons[0];

  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <div className={styles.avatarBlock}>
          {student.avatarUrl ? (
            <img src={student.avatarUrl} alt="" className={styles.avatar} />
          ) : (
            <div className={styles.avatarFallback} aria-hidden="true">
              {initials}
            </div>
          )}
          <span
            className={`${styles.severityDot} ${styles[`severity_${student.aggregateSeverity}`]}`}
            aria-label={`${student.aggregateSeverity} severity`}
            title={`${student.aggregateSeverity} severity`}
          />
        </div>

        <div className={styles.rowContent}>
          <div className={styles.nameLine}>
            <Link to={`/students/${student.studentId}`} className={styles.studentName}>
              {student.studentName}
            </Link>
          </div>
          <div className={styles.reasonRow}>
            {primaryReason && (
              <span className={styles.reasonBadge} title={primaryReason.detail}>
                {REASON_SHORT[primaryReason.type](primaryReason)}
              </span>
            )}
            {student.reasons.length > 1 && (
              <span
                className={styles.moreBadge}
                title={student.reasons
                  .slice(1)
                  .map((r) => `${REASON_LABEL[r.type]}: ${r.detail}`)
                  .join('\n')}
              >
                +{student.reasons.length - 1}
              </span>
            )}
          </div>
        </div>

        <div className={styles.rowMeta}>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.reachOut}
              onClick={onReachOut}
              aria-label={`Reach out to ${student.studentName}`}
            >
              Reach out
            </button>
            <div className={styles.dismissWrap}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={onToggleDismiss}
                aria-haspopup="menu"
                aria-expanded={dismissOpen}
                aria-label={`Dismiss a flag for ${student.studentName}`}
              >
                ⋯
              </button>
              {dismissOpen && (
                <div role="menu" className={styles.dismissMenu}>
                  {/* Backend dismissal is per-student for 24h, so the affordance
                      is a single honest action — not one entry per reason. */}
                  <button
                    role="menuitem"
                    type="button"
                    className={styles.dismissItem}
                    onClick={onDismiss}
                  >
                    Dismiss student for 24h
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export default AtRiskWidget;
